import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trash2, AlertTriangle, Shield, WifiOff } from 'lucide-react';
import { usePermissions } from '@/contexts/unified-permissions';
import { useOfflineState } from '@/contexts/offline/useOfflineState';
import { AccountDeletionModal } from './AccountDeletionModal';
import { AccountDeletionService } from '@/services/account/AccountDeletionService';
import { toast } from 'sonner';

export function AccountSettings() {
  const { user } = usePermissions();
  const { state } = useOfflineState();
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleDeleteAccount = () => {
    setShowDeletionModal(true);
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const result = await AccountDeletionService.exportUserData();

      if (result.success && result.data) {
        const downloaded = AccountDeletionService.downloadUserData(result.data);
        if (downloaded) {
          toast.success('Your data has been exported and downloaded successfully.');
        } else {
          toast.error('Failed to download the exported data.');
        }
      } else {
        toast.error(result.error || 'Failed to export your data.');
      }
    } catch (error) {
      toast.error('An error occurred while exporting your data.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Account Settings</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Manage your account security and data preferences.
        </p>
      </div>

      {/* Account Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Account Information
          </CardTitle>
          <CardDescription>
            Your account details and security information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Email Address</label>
              <p className="text-sm">{user?.email || 'Not available'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Account Created</label>
              <p className="text-sm">
                {user?.created_at 
                  ? new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : 'Not available'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data & Privacy Card */}
      <Card>
        <CardHeader className="text-left">
          <CardTitle className="text-lg text-left">Data & Privacy</CardTitle>
          <CardDescription className="text-left">
            Manage your data and privacy preferences
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 text-left">
              <h4 className="font-medium text-sm mb-1 text-left">Data Export</h4>
              <p className="text-sm text-muted-foreground text-left">
                Download a copy of your training data, techniques, and game plans.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportData}
              disabled={!state.isOnline || isExporting}
              title={!state.isOnline ? "Data export requires internet connection" : ""}
              className="ml-4 shrink-0"
            >
              {isExporting ? 'Exporting...' : 'Export Data'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone Card */}
      <Card className="border-destructive/20">
        <CardHeader className="text-left">
          <CardTitle className="text-lg text-destructive flex items-center gap-2 text-left">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
          <CardDescription className="text-left">
            Irreversible actions that will permanently affect your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!state.isOnline && (
            <Alert>
              <WifiOff className="h-4 w-4" />
              <AlertDescription className="text-left">
                Account deletion requires an active internet connection for security verification.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <div className="flex-1 text-left">
              <h4 className="font-medium text-sm mb-1 text-left">Delete Account</h4>
              <p className="text-sm text-muted-foreground text-left">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteAccount}
              disabled={!state.isOnline}
              className="ml-4 shrink-0 flex items-center gap-2"
              title={!state.isOnline ? "Account deletion requires internet connection" : ""}
            >
              <Trash2 className="h-4 w-4" />
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Deletion Modal */}
      <AccountDeletionModal 
        open={showDeletionModal}
        onOpenChange={setShowDeletionModal}
      />
    </div>
  );
}
